import { Input, Select } from '@/components/ui/FormControls';
import PermissionGate from '@/components/common/PermissionGate';
import { useState, useMemo } from 'react';
import Link from '@/components/common/AccessLink';
import { useProducts, useDeactivateProduct } from '@/hooks/useProducts';
import { useInventory } from '@/hooks/useInventory';
import { useProductTypes } from '@/hooks/useMasterConfig';
import PageHeader from '@/components/common/PageHeader';
import EmptyState from '@/components/common/EmptyState';
import { SkeletonTable } from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import StatusBadge from '@/components/common/StatusBadge';
import ExportActions from '@/components/common/ExportActions';
import {
  Plus,
  Package,
  Search,
  Pencil,
  Trash2,
  ChevronRight,
  Filter,
  Layers,
  Tag,
  Box,
  Eye,
  EyeOff,
  Boxes,
  AlertTriangle,
  Upload,
} from 'lucide-react';

import { useConfirm } from '@/contexts/ConfirmContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export default function ProductListPage() {
  const { role } = useAuth();
  const [search, setSearch] = useState('');
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<string>('');
  const [showInactive, setShowInactive] = useState(false);

  const { data: productTypes } = useProductTypes();
  const { data: products, isLoading, error, refetch } = useProducts(search, {
    product_type_id: selectedTypeFilter || undefined,
    showInactive,
  });
  const { data: inventory } = useInventory();

  // Variant inventory map for real-time available stock
  const inventoryMap = useMemo(() => {
    return new Map((inventory ?? []).map((i) => [i.variant_id, i]));
  }, [inventory]);

  const deactivate = useDeactivateProduct();
  const confirm = useConfirm();

  const handleDeactivate = async (id: string, name: string) => {
    const isOk = await confirm({
      title: 'Deactivate Product',
      description: `Deactivate product "${name}"? Existing stock and sales will be preserved. You can view it by turning on "Show Inactive".`,
      variant: 'destructive',
      confirmText: 'Deactivate',
    });
    if (!isOk) return;

    try {
      await deactivate.mutateAsync(id);
      toast.success(`Product "${name}" deactivated successfully`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to deactivate product';
      toast.error(msg);
    }
  };

  // ── Filter matching (UUID or name-based fallback) ──────────────
  const getProductTypeName = (product: any): string => {
    if (!productTypes || productTypes.length === 0) return '—';
    const pType = productTypes.find(
      (pt: any) =>
        (pt as any).id === product.product_type_id ||
        pt.code === product.product_type_id ||
        pt.name === product.product_type_id
    );
    return pType?.name ?? (product.product_type_id || '—');
  };

  // ── Stock helper: total available stock across all variants ─────
  const getTotalStock = (product: any): number => {
    if (!product.product_variants || product.product_variants.length === 0) {
      return Number(product.total_available ?? product.total_stock ?? 0);
    }
    return product.product_variants.reduce((sum: number, v: any) => {
      const inv = inventoryMap.get(v.id);
      return sum + Number(inv?.quantity_available ?? v.available ?? v.on_hand ?? 0);
    }, 0);
  };

  // ── Summary info for exports ────────────────────────────────────
  const selectedTypeName = productTypes?.find(
    (pt: any) => ((pt as any).id || pt.code || pt.name) === selectedTypeFilter
  )?.name;
  const filterSummary = [
    selectedTypeName ? `Category: ${selectedTypeName}` : null,
    search ? `Search: "${search}"` : null,
    showInactive ? 'Showing: All (incl. Inactive)' : 'Showing: Active Only',
  ].filter(Boolean).join(' | ') || 'All Products';

  // ── Apply in-memory type filtering for non-UUID type IDs ────────
  const filteredProducts = (() => {
    if (!products) return [];
    if (!selectedTypeFilter) return products;

    const isValidUUID = (str?: string | null) =>
      str ? /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str) : false;

    if (isValidUUID(selectedTypeFilter)) return products; // already filtered by DB

    // Fallback: match by name of selected type
    const selectedType = productTypes?.find(
      (pt: any) => ((pt as any).id || pt.code || pt.name) === selectedTypeFilter
    );
    const typeName = selectedType?.name?.toLowerCase();
    const typeCode = (selectedType as any)?.code?.toLowerCase();

    return products.filter((p: any) => {
      const ptId = p.product_type_id?.toLowerCase();
      return (
        ptId === selectedTypeFilter.toLowerCase() ||
        ptId === typeName ||
        ptId === typeCode ||
        (p.attributes?.PRODUCT_TYPE ?? p.attributes?.product_type)?.toLowerCase() === typeName
      );
    });
  })();

  // ── Excel export helper ─────────────────────────────────────────
  const getExcelVariantRows = () => {
    const rows: (string | number)[][] = [];
    (filteredProducts ?? []).forEach((p: any) => {
      if (p.product_variants && p.product_variants.length > 0) {
        p.product_variants.forEach((v: any) => {
          const inv = inventoryMap.get(v.id);
          const availableStock = Number(inv?.quantity_available ?? v.available ?? v.on_hand ?? 0);
          rows.push([
            p.product_code || '—',
            p.name,
            v.sku || '—',
            v.size || '—',
            v.color || '—',
            Number(v.default_cost || 0),
            Number(v.default_price || 0),
            availableStock,
            p.is_active ? 'Active' : 'Inactive',
          ]);
        });
      } else {
        rows.push([
          p.product_code || '—',
          p.name,
          '—', '—', '—',
          0, 0,
          Number(p.total_available ?? 0),
          p.is_active ? 'Active' : 'Inactive',
        ]);
      }
    });
    return rows;
  };

  const activeCount = products?.filter((p: any) => p.is_active).length ?? 0;
  const inactiveCount = products?.filter((p: any) => !p.is_active).length ?? 0;

  const exportProductsData = useMemo(
    () =>
      (filteredProducts ?? []).map((p: any) => ({
        ...p,
        product_code: p.product_code || '—',
        name: p.name,
        product_type: p.product_types?.name ?? p.product_type ?? '—',
        category_name: p.categories?.name ?? p.category_name ?? '—',
        variant_count: p.product_variants?.length ?? 0,
        default_price: Number(p.product_variants?.[0]?.default_price ?? p.default_price ?? 0),
        total_stock: getTotalStock(p),
        status: p.is_active ? 'Active' : 'Inactive',
      })),
    [filteredProducts]
  );

  return (
    <div>
      <PageHeader
        title="Products Catalog"
        description="Manage your multi-category clothing catalog and variants"
        actions={
          <div className="flex items-center gap-2">
            <ExportActions
              exportType="products"
              data={exportProductsData as unknown as Record<string, unknown>[]}
              excelOptions={() => ({
                fileName: `Products_Catalog_${new Date().toISOString().split('T')[0]}`,
                sheetName: 'Products & Variants',
                filtersSummary: filterSummary,
                columns: [
                  { header: 'Code',               key: 'code' },
                  { header: 'Product Name',        key: 'name' },
                  { header: 'SKU',                 key: 'sku' },
                  { header: 'Size',                key: 'size' },
                  { header: 'Color',               key: 'color' },
                  { header: 'Default Cost (₹)',    key: 'cost',    format: 'currency', width: 18 },
                  { header: 'Default Price (₹)',   key: 'price',   format: 'currency', width: 18 },
                  { header: 'Stock Available',     key: 'stock',   format: 'integer',  width: 16 },
                  { header: 'Status',              key: 'status' },
                ],
                data: getExcelVariantRows().map((row) => ({
                  code:   row[0],
                  name:   row[1],
                  sku:    row[2],
                  size:   row[3],
                  color:  row[4],
                  cost:   row[5],
                  price:  row[6],
                  stock:  row[7],
                  status: row[8],
                })),
              })}
            />
            {(role === 'owner' || role === 'admin') && (
              <Link
                to="/products/import"
                className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-accent"
              >
                <Upload className="h-4 w-4" />
                Import
              </Link>
            )}
            <Link
              to="/products/new"
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" />
              Add Product
            </Link>
          </div>
        }
      />

      {/* Filters & Search Bar */}
      <div className="obsidian-surface mb-6 rounded-xl bg-card border border-border/50 shadow-sm overflow-hidden">
        {/* Active / Inactive tabs */}
        <div className="flex items-center border-b border-border/50 px-4 pt-3 gap-1">
          <button
            onClick={() => setShowInactive(false)}
            className={`px-4 py-2 rounded-t-md text-sm font-medium transition-colors border-b-2 -mb-px ${
              !showInactive
                ? 'border-primary text-primary bg-primary/5'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <span className="flex items-center gap-1.5">
              <Eye className="h-3.5 w-3.5" />
              Active
              {activeCount > 0 && (
                <span className="inline-flex items-center justify-center rounded-full bg-primary/10 text-primary text-[10px] font-bold px-1.5 min-w-[18px]">
                  {activeCount}
                </span>
              )}
            </span>
          </button>
          <button
            onClick={() => setShowInactive(true)}
            className={`px-4 py-2 rounded-t-md text-sm font-medium transition-colors border-b-2 -mb-px ${
              showInactive
                ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-500/5'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <span className="flex items-center gap-1.5">
              <EyeOff className="h-3.5 w-3.5" />
              All (incl. Inactive)
              {showInactive && inactiveCount > 0 && (
                <span className="inline-flex items-center justify-center rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 text-[10px] font-bold px-1.5 min-w-[18px]">
                  {inactiveCount}
                </span>
              )}
            </span>
          </button>
        </div>

        {/* Search + Type Filter row */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              id="product-search"
              placeholder="Search by name, code, team, or attributes…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex h-10 w-full rounded-lg border border-input bg-background pl-10 pr-4 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 hover:border-input/80 transition-colors"
              helpLabel="Search records"
            />
          </div>

          {/* Product Type Filter */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <Filter className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Select
                id="product-type-filter"
                value={selectedTypeFilter}
                onChange={(e) => setSelectedTypeFilter(e.target.value)}
                className="h-10 appearance-none rounded-lg border border-input bg-background pl-10 pr-10 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 hover:border-input/80 transition-colors cursor-pointer"
                helpLabel="Filter product category"
              >
                <option value="">All Categories</option>
                {productTypes?.map((pt) => {
                  const ptId = (pt as any).id || pt.code || pt.name;
                  return (
                    <option key={ptId} value={ptId}>
                      {pt.name}
                    </option>
                  );
                })}
              </Select>
              <ChevronRight className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 rotate-90 text-muted-foreground pointer-events-none" />
            </div>

            {/* Clear filters pill */}
            {(selectedTypeFilter || search) && (
              <button
                onClick={() => { setSelectedTypeFilter(''); setSearch(''); }}
                className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-muted/40 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted transition-colors"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* States */}
      {isLoading && <SkeletonTable rows={5} />}
      {error && <ErrorState message={error.message} onRetry={refetch} />}

      {!isLoading && !error && filteredProducts.length === 0 && (
        <EmptyState
          icon={<Package className="h-12 w-12" />}
          title={search || selectedTypeFilter ? 'No products match your filters' : showInactive ? 'No products found' : 'No active products'}
          description={
            search || selectedTypeFilter
              ? 'Try clearing your search or category filter'
              : showInactive
              ? 'No products have been created yet'
              : 'All products may be inactive — toggle "All (incl. Inactive)" to see them'
          }
          action={
            !search && !selectedTypeFilter ? (
              <Link
                to="/products/new"
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
              >
                <Plus className="h-4 w-4" />
                Add Product
              </Link>
            ) : undefined
          }
        />
      )}

      {/* Table */}
      {!isLoading && !error && filteredProducts.length > 0 && (
        <div className="obsidian-surface overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm transition-shadow hover:shadow-md">
          {/* Desktop table */}
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm">
              <thead className="border-b border-border/60 bg-muted/30">
                <tr>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Product</th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Category</th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Attributes</th>
                  <th className="px-5 py-4 text-center text-xs font-bold uppercase tracking-wider text-muted-foreground">Variants</th>
                  <th className="px-5 py-4 text-center text-xs font-bold uppercase tracking-wider text-muted-foreground">Stock</th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Status</th>
                  <th className="px-5 py-4 text-right text-xs font-bold uppercase tracking-wider text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {filteredProducts.map((p: any) => {
                  const pTypeName = getProductTypeName(p);
                  const totalStock = getTotalStock(p);
                  const isLowStock = totalStock > 0 && totalStock <= 10;
                  const isOutOfStock = totalStock === 0;

                  // Collect attribute values (max 2 visible + overflow badge)
                  const attrs = p.attributes && typeof p.attributes === 'object' ? p.attributes : {};
                  const attrEntries = Object.entries(attrs)
                    .filter(([k, v]) => v && String(v).trim() && !['PRODUCT_TYPE', 'product_type', 'TARGET_AUDIENCE', 'AGE_GROUP'].includes(k));
                  const visibleAttrs = attrEntries.slice(0, 2);
                  const overflowCount = attrEntries.length - visibleAttrs.length;
                  const allAttrText = attrEntries.map(([, v]) => String(v)).join(', ');

                  return (
                    <tr
                      key={p.id}
                      className={`transition-colors hover:bg-muted/40 group ${!p.is_active ? 'opacity-60' : ''}`}
                    >
                      {/* Product (Name + Code as subtitle) */}
                      <td className="px-5 py-4">
                        <Link
                          to={`/products/${p.id}`}
                          className="font-semibold text-foreground hover:text-primary transition-colors"
                        >
                          {p.name}
                        </Link>
                        <p className="mt-0.5 font-mono text-[10px] tracking-wide text-muted-foreground/70">
                          {p.product_code}
                        </p>
                      </td>

                      {/* Product Type */}
                      <td className="px-5 py-4">
                        <span className="inline-flex items-center gap-1.5 rounded-md bg-muted/60 px-2.5 py-1 text-[11px] font-medium text-foreground border border-border/50">
                          <Tag className="h-3 w-3 text-muted-foreground" />
                          {pTypeName}
                        </span>
                      </td>

                      {/* Attributes — max 2 visible + overflow badge */}
                      <td className="px-5 py-4 text-xs text-muted-foreground max-w-[180px]">
                        {visibleAttrs.length > 0 ? (
                          <div className="flex flex-wrap gap-1" title={allAttrText}>
                            {visibleAttrs.map(([k, v]) => (
                              <span
                                key={k}
                                className="inline-flex items-center rounded bg-muted/50 px-1.5 py-0.5 text-[10px] font-medium text-foreground/70 border border-border/30"
                              >
                                {String(v)}
                              </span>
                            ))}
                            {overflowCount > 0 && (
                              <span
                                className="inline-flex items-center rounded bg-primary/5 px-1.5 py-0.5 text-[10px] font-semibold text-primary border border-primary/10"
                                title={allAttrText}
                              >
                                +{overflowCount}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="italic opacity-40 text-[11px]">No attributes</span>
                        )}
                      </td>

                      {/* Variants count */}
                      <td className="px-5 py-4 text-center">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                          <Layers className="h-3.5 w-3.5" />
                          {p.product_variants?.length ?? 0}
                        </span>
                      </td>

                      {/* Stock */}
                      <td className="px-5 py-4 text-center">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold border ${
                            isOutOfStock
                              ? 'bg-destructive/10 text-destructive border-destructive/20'
                              : isLowStock
                              ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
                              : 'bg-muted/40 text-foreground border-border/40'
                          }`}
                        >
                          {isOutOfStock && <AlertTriangle className="h-3 w-3" />}
                          {isLowStock && !isOutOfStock && <AlertTriangle className="h-3 w-3" />}
                          {!isLowStock && !isOutOfStock && <Boxes className="h-3 w-3 text-muted-foreground" />}
                          {isOutOfStock ? 'Out' : totalStock}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-5 py-4">
                        <StatusBadge status={p.is_active ? 'active' : 'inactive'} />
                      </td>

                      {/* Actions — always visible (not hover-only) */}
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-1.5">
                          <Link
                            to={`/products/${p.id}/edit`}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
                            title="Edit product"
                            aria-label={`Edit ${p.name}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Link>
                          {p.is_active && (
                            <PermissionGate permission="products.delete"><button
                              onClick={() => handleDeactivate(p.id, p.name)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                              title="Deactivate product"
                              aria-label={`Deactivate ${p.name}`}
                              disabled={deactivate.isPending}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button></PermissionGate>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile card layout */}
          <div className="divide-y divide-border/40 md:hidden bg-card">
            {filteredProducts.map((p: any) => {
              const pTypeName = getProductTypeName(p);
              const totalStock = getTotalStock(p);
              const isOutOfStock = totalStock === 0;
              const isLowStock = totalStock > 0 && totalStock <= 10;

              return (
                <div
                  key={p.id}
                  className={`flex flex-col gap-3 p-4 ${!p.is_active ? 'opacity-60' : ''}`}
                >
                  {/* Top row: code + name + actions */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex flex-col gap-1 min-w-0">
                      <span className="inline-flex w-fit items-center rounded-md bg-primary/5 px-2 py-0.5 text-[10px] font-semibold font-mono tracking-wide text-primary border border-primary/10">
                        {p.product_code}
                      </span>
                      <Link to={`/products/${p.id}`} className="font-semibold text-foreground hover:text-primary transition-colors truncate">
                        {p.name}
                      </Link>
                    </div>
                    {/* Mobile actions — always visible */}
                    <div className="flex items-center gap-1 shrink-0">
                      <Link
                        to={`/products/${p.id}/edit`}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/5 text-primary border border-primary/10 transition-colors hover:bg-primary/10"
                        title="Edit"
                        aria-label={`Edit ${p.name}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Link>
                      {p.is_active && (
                        <PermissionGate permission="products.delete"><button
                          onClick={() => handleDeactivate(p.id, p.name)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-destructive/5 text-destructive border border-destructive/10 transition-colors hover:bg-destructive/10"
                          title="Deactivate"
                          aria-label={`Deactivate ${p.name}`}
                          disabled={deactivate.isPending}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button></PermissionGate>
                      )}
                    </div>
                  </div>

                  {/* Bottom row: type, variants, stock, status */}
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1 rounded-md bg-muted/60 px-2 py-0.5 border border-border/50">
                      <Tag className="h-3 w-3" />
                      {pTypeName}
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-0.5 font-medium text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                      <Layers className="h-3 w-3" />
                      {p.product_variants?.length ?? 0} variants
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 font-medium border ${
                        isOutOfStock
                          ? 'bg-destructive/10 text-destructive border-destructive/20'
                          : isLowStock
                          ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
                          : 'bg-muted/40 text-foreground border-border/40'
                      }`}
                    >
                      <Box className="h-3 w-3" />
                      {isOutOfStock ? 'Out of stock' : `${totalStock} in stock`}
                    </span>
                    <StatusBadge status={p.is_active ? 'active' : 'inactive'} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Count footer */}
      {!isLoading && filteredProducts.length > 0 && (
        <p className="mt-3 text-xs text-muted-foreground">
          {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
          {search && ` matching "${search}"`}
          {selectedTypeFilter && selectedTypeName && ` in ${selectedTypeName}`}
        </p>
      )}
    </div>
  );
}
